<template>
    <div class="border border-border bg-surface-raised rounded-lgr py-10 px-5 mb-4">
        <h3 class="title-subsection text-ink">{{ title }}</h3>
        <HorizontalDivider as="hr" />
        <div class="body text-ink">{{ subtitle }}</div>
        <div class="body-small text-ink-muted mt-2 mb-4">{{ t("analytics.insights.drivers.heading") }}</div>
        <div class="relative w-full h-72 md:h-96.75">
            <div class="absolute top-0 left-0 w-full h-full">
                <canvas ref="driversChart"></canvas>
            </div>
            <SkeletonLoader v-if="isLoading" class="h-full w-full rounded-lgr" />
            <div v-if="!isLoading && !drivers.length" class="absolute top-0 left-0 w-full h-full p-2 flex items-center justify-center text-ink-muted">
                {{ t("analytics.charts.noData") }}
            </div>
        </div>
    </div>
    <div class="border border-border bg-surface-raised rounded-lgr py-10 px-5">
        <SkeletonLoader v-if="isLoading" class="h-7 w-full rounded-lgr" />
        <div v-else class="title-subsection text-ink">{{ selectedDriver?.label }} {{ overTimeTitleSuffix ?? t("analytics.insights.drivers.overTimeSuffix") }}</div>
        <div class="body-small text-ink-muted mt-2 mb-3">{{ t("analytics.insights.drivers.overTimeHeading") }}</div>
        <div class="relative w-full mt-5 h-72 md:h-88.75">
            <div class="absolute top-0 left-0 w-full h-full border border-border rounded-lgr py-4 px-5">
                <canvas ref="overTimeChart"></canvas>
            </div>
            <SkeletonLoader v-if="isLoading" class="h-full w-full rounded-lgr" />
            <div v-if="!isLoading && !selectedDriver" class="absolute top-0 left-0 w-full h-full p-2 flex items-center justify-center text-ink-muted">
                {{ t("analytics.charts.noData") }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import "chartjs-adapter-luxon"; // Needed for TimeScale
import { cssColor } from "~vue/ChatHome/workiqTrendColors";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { BarController, BarElement, CategoryScale, Chart, LinearScale, TimeScale, Title } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

type Driver = {
    label: string;
    color?: string;
    user_percentage: number;
    history: Array<{
        date: string;
        user_count: number;
        user_percentage: number;
    }>;
};

const { t } = useI18n();

const {
    title,
    subtitle,
    overTimeTitleSuffix,
    overTimeSubtitlePrefix,
    isLoading = false,
    drivers = [],
    responsive = true,
} = defineProps<{
    title: string;
    subtitle: string;
    overTimeTitleSuffix?: string;
    overTimeSubtitlePrefix: string;
    isLoading?: boolean;
    drivers?: Driver[];
    responsive?: boolean; // Used only in testing to ensure we catch missing registrations
}>();

const getBiggestDriver = (drivers: Driver[]): Driver | null => {
    return drivers.reduce<Driver | null>((maxDriver, currentDriver) => {
        if (maxDriver === null || currentDriver.user_percentage > maxDriver.user_percentage) {
            return currentDriver;
        }
        return maxDriver;
    }, null);
};

const selectedDriver = ref<Driver | null>(getBiggestDriver(drivers));

watch(
    () => drivers,
    (newDrivers) => {
        const currentLabel = selectedDriver.value?.label;
        if (currentLabel) {
            const matchingDriver = newDrivers.find((driver) => driver.label === currentLabel);
            if (matchingDriver) {
                selectedDriver.value = matchingDriver;
                return;
            }
        }

        selectedDriver.value = getBiggestDriver(newDrivers);
    },
);

const splitToTwoLines = (text: string): string | string[] => {
    text = text.trim();
    if (!text.includes(" ") || text.length <= 20) {
        return text;
    }
    // Find the space character closest to the middle of the string.
    let leftIndex = Math.floor(text.length / 2);
    let rightIndex = leftIndex + 1;
    while (leftIndex >= 0 && rightIndex < text.length) {
        // Prefer to split on the right side so that the top line is longer.
        if (text[rightIndex] === " ") {
            return [text.slice(0, rightIndex), text.slice(rightIndex + 1)];
        } else if (text[leftIndex] === " ") {
            return [text.slice(0, leftIndex), text.slice(leftIndex + 1)];
        }
        leftIndex--;
        rightIndex++;
    }
    // Should never get here, but just to keep the type checker happy...
    return text;
};

const driversCanvasRef = useTemplateRef("driversChart");
const driversChartRef = ref<Chart | null>(null);
const overTimeCanvasRef = useTemplateRef("overTimeChart");
const overTimeChartRef = ref<Chart | null>(null);

const updateDriversChart = (newIsLoading: boolean, newDrivers: Driver[], newSelectedDriver: Driver | null) => {
    if (!driversCanvasRef.value) {
        return;
    }
    if (driversChartRef.value) {
        driversChartRef.value.destroy();
    }
    if (newIsLoading || !newDrivers.length) {
        return;
    }

    newDrivers.sort((a, b) => b.user_percentage - a.user_percentage);

    driversChartRef.value = new Chart(driversCanvasRef.value, {
        type: "bar",
        data: {
            labels: newDrivers.map((driver) => splitToTwoLines(driver.label)),
            datasets: [
                {
                    data: newDrivers.map((driver) => Math.round(driver.user_percentage * 10) / 10),
                    backgroundColor: newDrivers.map((driver) =>
                        driver.label === newSelectedDriver?.label ? driver.color || cssColor("--color-border", "#E8E8E8") : cssColor("--color-border", "#E8E8E8"),
                    ),
                    borderRadius: {
                        topRight: 3,
                        bottomRight: 3,
                    },
                    barPercentage: 0.75,
                },
            ],
        },
        options: {
            // Unfortunately, Chart.js breaks if you interact or switch subtabs during an animation.
            animation: false,
            responsive,
            maintainAspectRatio: false,
            indexAxis: "y",
            scales: {
                x: {
                    beginAtZero: true,
                    grace: "20%",
                    ticks: {
                        font: {
                            family: "Inter, sans-serif",
                            size: 12,
                        },
                        color: cssColor("--color-ink-muted", "#595959"),
                        callback: (value) => `${value}%`,
                        maxTicksLimit: 6,
                    },
                },
                y: {
                    ticks: {
                        font: {
                            family: "Inter, sans-serif",
                            size: 14,
                        },
                        color: cssColor("--color-ink", "#262626"),
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
                datalabels: {
                    display: true,
                    align: "right",
                    anchor: "end",
                    color: cssColor("--color-ink", "#262626"),
                    font: {
                        family: "Inter, sans-serif",
                        size: 14,
                    },
                    formatter: (value) => `${value}%`,
                },
            },
            interaction: {
                intersect: false,
                mode: "nearest",
                axis: "y",
            },
            onHover: (evt, elements) => {
                const target = evt.native?.target as HTMLCanvasElement | null;
                if (!target) {
                    return;
                }
                target.style.cursor = elements.length > 0 ? "pointer" : "default";
            },
            onClick: (evt, elements) => {
                if (elements.length === 0) {
                    return;
                }
                const element = elements[elements.length - 1];
                if (!element) {
                    return;
                }
                const dataset = driversChartRef.value?.data.datasets[element.datasetIndex];
                let clickedLabel = driversChartRef.value?.data.labels?.[element.index];
                if (!dataset || !clickedLabel) {
                    return;
                }
                if (Array.isArray(clickedLabel)) {
                    clickedLabel = clickedLabel.join(" ");
                }
                const clickedDriver = newDrivers.find((driver) => driver.label === clickedLabel);
                if (clickedDriver) {
                    selectedDriver.value = clickedDriver;
                }
            },
        },
    });
};

const updateOverTimeChart = (newIsLoading: boolean, newSelectedDriver: Driver | null) => {
    if (!overTimeCanvasRef.value) {
        return;
    }
    if (overTimeChartRef.value) {
        overTimeChartRef.value.destroy();
    }
    if (newIsLoading || !newSelectedDriver) {
        return;
    }

    overTimeChartRef.value = new Chart(overTimeCanvasRef.value, {
        type: "bar",
        data: {
            labels: newSelectedDriver.history.map((entry) => entry.date),
            datasets: [
                {
                    data: newSelectedDriver.history.map((entry) => Math.round(entry.user_percentage * 10) / 10),
                    backgroundColor: newSelectedDriver.color || cssColor("--color-border", "#E8E8E8"),
                    borderRadius: {
                        topLeft: 3,
                        topRight: 3,
                    },
                    barPercentage: 0.75,
                },
            ],
        },
        options: {
            animation: false,
            responsive,
            maintainAspectRatio: false,
            scales: {
                x: {
                    type: "time",
                    time: {
                        unit: "month",
                    },
                    grid: {
                        display: false,
                    },
                    ticks: {
                        font: {
                            family: "Inter, sans-serif",
                            size: 12,
                        },
                        color: cssColor("--color-ink-muted", "#595959"),
                    },
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        font: {
                            family: "Inter, sans-serif",
                            size: 12,
                        },
                        color: cssColor("--color-ink-muted", "#595959"),
                        maxTicksLimit: 6,
                        callback: (value) => `${value}%`,
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
                title: {
                    align: "start",
                    display: true,
                    text: `${overTimeSubtitlePrefix} ${newSelectedDriver.label}`,
                    color: cssColor("--color-ink", "#262626"),
                    font: {
                        family: "Inter, sans-serif",
                        size: 14,
                        weight: "normal",
                    },
                    padding: {
                        top: 0,
                        bottom: 16,
                    },
                },
                datalabels: {
                    display: false,
                },
            },
        },
    });
};

watch([() => isLoading, () => drivers, selectedDriver, () => overTimeSubtitlePrefix], ([newIsLoading, newDrivers, newSelectedDriver]) => {
    updateDriversChart(newIsLoading, newDrivers, newSelectedDriver);
    updateOverTimeChart(newIsLoading, newSelectedDriver);
});

const loadHandler = () => {
    updateDriversChart(isLoading, drivers, selectedDriver.value);
    updateOverTimeChart(isLoading, selectedDriver.value);
};

onMounted(() => {
    // Chart.js is smart enough to only register this globally once.
    Chart.register(ChartDataLabels, CategoryScale, BarController, LinearScale, BarElement, TimeScale, Title);

    // Wait for the document to be loaded before rendering the chart to ensure fonts are loaded.
    // Otherwise, the chart can render with the wrong font at first and jank on the first mouseover.
    if (document.readyState === "complete") {
        loadHandler();
    } else {
        window.addEventListener("load", loadHandler);
    }
});

onUnmounted(() => {
    driversChartRef.value?.destroy();
    overTimeChartRef.value?.destroy();
    window.removeEventListener("load", loadHandler);
});
</script>
